import { Permissions, QUERY_KEYS, RoleUser } from "@cnx/common";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { HttpStatusCode, isAxiosError } from "axios";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import UserService from "services/UserService";
import { MutationHookOptions } from "types/QueryHookOptions";
import { showError, showSuccess } from "utils/index";

export const useUsers = () => {
  const { t } = useTranslation("users");
  const { error, data, isPending, isSuccess, isRefetching } = useQuery({
    queryKey: [QUERY_KEYS.USERS],

    queryFn: () => {
      return UserService.fetchUsers();
    },
  });

  useEffect(() => {
    if (!error) return;
    showError(t("fetchFailed"));
  }, [error]);

  return { users: data ?? [], isPending, isSuccess, isRefetching };
};

export const useUsersMutation = ({ onSuccess }: MutationHookOptions) => {
  const { t } = useTranslation("users");
  const queryClient = useQueryClient();
  const { data, isPending } = useMutation({
    mutationFn: ({ user, id }: { user: FormData; id?: string }) => {
      if (id) {
        return UserService.editUser(user, id);
      }
      return UserService.addUser(user);
    },

    onSuccess: async () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.USERS],
      });
      showSuccess(t("saveSuccess"));
      onSuccess();
    },

    onError: error => {
      if (!isAxiosError(error)) {
        showError(t("saveFailed"));
        return;
      }

      if (error.response?.status === HttpStatusCode.Forbidden) {
        showError(t("fetchFailedNoRights"));
      }
    },
  });

  return { data, isPending };
};

export const useUsersWithRight = (right: Permissions) => {
  const { t } = useTranslation("users");
  const { error, data, isPending } = useQuery({
    queryKey: [QUERY_KEYS.USERS, "withRight", right],
    queryFn: () => UserService.fetchUsersWithRight(right),
  });

  useEffect(() => {
    if (!error) return;
    showError(t("fetchFailed"));
  }, [error]);

  return { users: data ?? [], isPending };
};

export const useUsersWithRoles = (
  roleId?: string
): { users: RoleUser[]; isPending: boolean } => {
  const queryKey = roleId
    ? [QUERY_KEYS.USERS, "withRoles", roleId]
    : [QUERY_KEYS.USERS, "withRoles"];
  const { data, isPending } = useQuery({
    queryKey,
    queryFn: () => UserService.fetchUsersWithRoles(roleId),
    enabled: roleId === undefined || Boolean(roleId),
  });

  return { users: data ?? [], isPending };
};
